import React from 'react';
import {Box, FormControlLabel, Grid, Paper, Switch, Typography, Stack, Divider, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import {LineChart} from '@mui/x-charts';
import dayjs from 'dayjs';
import {IGunlukVeri} from '../hooks/useMaliRaporlar';
import {ShowChart, FilterAlt, ExpandMore} from '@mui/icons-material';

interface ShowSeriesState {
    satis: boolean;
    gider: boolean;
    mas15: boolean;
    mas30: boolean;
    mas90: boolean;
    mag15: boolean;
    mag30: boolean;
    mag90: boolean;
}

export const GunlukVeriFields: { key: keyof ShowSeriesState; label: string; color: string }[] = [
    {key: 'satis', label: 'Günlük Satış', color: '#1b5e20'},
    {key: 'gider', label: 'Günlük Gider', color: '#b71c1c'},
    {key: 'mas15', label: '15G Satış Ort.', color: '#4caf50'},
    {key: 'mas30', label: '30G Satış Ort.', color: '#2e7d32'},
    {key: 'mas90', label: '90G Satış Ort.', color: '#1b5e20'},
    {key: 'mag15', label: '15G Gider Ort.', color: '#ef5350'},
    {key: 'mag30', label: '30G Gider Ort.', color: '#d32f2f'},
    {key: 'mag90', label: '90G Gider Ort.', color: '#b71c1c'},
];

interface GelirlerTablosuProps {
    baslangicTarih: string;
    setBaslangicTarih: (val: string) => void;
    bitisTarih: string;
    setBitisTarih: (val: string) => void;
    showSeries: ShowSeriesState;
    toggleSeries: (seriesName: keyof ShowSeriesState) => void;
    seciliTarihler: string[];
    gunlukVeri: Record<string, IGunlukVeri>;
}

const GelirlerTablosu: React.FC<GelirlerTablosuProps> = ({
                                                             baslangicTarih,
                                                             setBaslangicTarih,
                                                             bitisTarih,
                                                             setBitisTarih,
                                                             showSeries,
                                                             toggleSeries,
                                                             seciliTarihler,
                                                             gunlukVeri,
                                                         }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    
    const chartData = React.useMemo(() => {
        return seciliTarihler.map(tarih => {
            const data = gunlukVeri[tarih];
            return {
                date: dayjs(tarih, 'DD-MM-YYYY').toDate(),
                ...data
            };
        });
    }, [seciliTarihler, gunlukVeri]);

    const series = GunlukVeriFields.filter((f) => showSeries[f.key]).map((f) => ({
        data: chartData.map((d: any) => (d as any)[f.key] || 0),
        label: f.label,
        yAxisId: 'leftAxisId',
        color: f.color,
        showMark: false,
        area: f.key === 'satis' || f.key === 'gider'
    }));

    const yAxisMinMax = React.useMemo(() => {
        if (series.length === 0) return { min: 0, max: 100 };
        
        let min = Infinity;
        let max = -Infinity;
        
        series.forEach(s => {
            s.data.forEach(val => {
                if (val < min) min = val;
                if (val > max) max = val;
            });
        });
        
        if (min === Infinity) return { min: 0, max: 100 };
        
        // Biraz pay bırakalım
        const range = max - min;
        const padding = range * 0.1 || 10;
        
        return {
            min: Math.max(0, Math.floor(min - padding)),
            max: Math.ceil(max + padding)
        };
    }, [series]);

    const FilterContent = (
        <Grid container>
            {GunlukVeriFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} lg={12} key={'Switch-' + field.key}>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showSeries[field.key]}
                                onChange={() => toggleSeries(field.key)}
                                color="primary"
                            />
                        }
                        label={<Typography variant="body2" fontWeight={600} color="textSecondary" sx={{ fontSize: '0.75rem' }}>{field.label}</Typography>}
                        sx={{ my: 0.1 }}
                    />
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                alignItems={{ xs: 'flex-start', sm: 'center' }} 
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ShowChart color="primary" />
                    <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Gelir-Gider Grafiği</Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <DatePicker
                        label="Başlangıç"
                        value={dayjs(baslangicTarih, 'DD-MM-YYYY')}
                        onChange={(newValue) => setBaslangicTarih(newValue?.format('DD-MM-YYYY') ?? '')}
                        maxDate={dayjs(bitisTarih, 'DD-MM-YYYY')}
                        slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 170 }, bgcolor: 'white', borderRadius: '8px' } } }}
                    />
                    <DatePicker
                        label="Bitiş"
                        value={dayjs(bitisTarih, 'DD-MM-YYYY')}
                        onChange={(newValue) => setBitisTarih(newValue?.format('DD-MM-YYYY') ?? '')}
                        maxDate={dayjs()}
                        minDate={dayjs(baslangicTarih, 'DD-MM-YYYY')}
                        slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 170 }, bgcolor: 'white', borderRadius: '8px' } } }}
                    />
                </Stack>
            </Stack>

            <Divider sx={{ mb: 3, opacity: 0.6 }} />

            <Grid container spacing={3}>
                <Grid item xs={12} lg={2.5}>
                    {isDesktop ? (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.03)' }}>
                            <Typography variant="overline" fontWeight={800} color="textSecondary" sx={{ mb: 1, display: 'center', alignItems: 'center', gap: 0.5 }}>
                                <FilterAlt fontSize="small" /> Filtreler
                            </Typography>
                            {FilterContent}
                        </Box>
                    ) : (
                        <Accordion sx={{ bgcolor: 'grey.50', borderRadius: '12px !important', boxShadow: 'none', border: '1px solid rgba(0,0,0,0.03)' }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="overline" fontWeight={800} color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FilterAlt fontSize="small" /> Filtreler
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {FilterContent}
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Grid>

                <Grid item xs={12} lg={9.5}>
                    <Box sx={{ width: '100%', height: 450 }}>
                        <LineChart
                            series={series}
                            xAxis={[
                                {
                                    scaleType: 'time',
                                    data: chartData.map((d: any) => d.date),
                                    valueFormatter: (value: any) => dayjs(value).format('DD/MM/YY'),
                                },
                            ]}
                            yAxis={[{
                                id: 'leftAxisId',
                                min: yAxisMinMax.min,
                                max: yAxisMinMax.max
                            }]}
                            height={400}
                            margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                            slotProps={{
                                legend: {
                                    hidden: true
                                }
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default GelirlerTablosu;
